import React, { useEffect, useState, memo, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { CalendarIcon, ClockIcon, MapPinIcon, UsersIcon, TagIcon, ShareIcon, ArrowLeftIcon, FacebookIcon, TwitterIcon, LinkedinIcon } from 'lucide-react';
// Sample events data
const eventsData = [{
  id: 1,
  slug: 'sports-competition',
  title: 'Annual Inter-House Sports Competition',
  date: '2023-11-10',
  time: '9:00 AM - 4:00 PM',
  location: 'School Sports Ground',
  category: 'sports',
  tags: ['Sports', 'Competition', 'Inter-House'],
  image: 'https://placehold.co/1200x800/navy/white?text=Sports+Competition',
  description: 'Join us for our annual Inter-House Sports Competition featuring athletics, football, volleyball, and more exciting sporting activities...',
  content: `<p>We are excited to announce that the Annual Inter-House Sports Competition for the 2023/2024 academic session will be held on November 10, 2023, at the school sports ground.</p>
    <p>This highly anticipated event brings together students, staff, parents, and alumni for a day of friendly competition, sportsmanship, and school spirit. The four houses - Ransome-Kuti (Red), Awolowo (Blue), Azikiwe (Green), and Bello (Yellow) - will compete for the coveted Champions Trophy.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> November 10, 2023</li>
      <li><strong>Time:</strong> 9:00 AM - 4:00 PM</li>
      <li><strong>Venue:</strong> School Sports Ground</li>
      <li><strong>Chief Host:</strong> Mrs. Adebisi Ogunlana, Principal</li>
      <li><strong>Special Guest of Honor:</strong> Dr. Oluwasegun Adeyemi, Chairman, School Board of Governors</li>
    </ul>
    <h3>Competition Categories:</h3>
    <ul>
      <li>Track Events (100m, 200m, 400m, 800m, relays)</li>
      <li>Field Events (long jump, high jump, shot put, javelin)</li>
      <li>Team Sports (football, volleyball, basketball)</li>
      <li>Special Events (tug-of-war, sack race, egg-and-spoon race)</li>
      <li>Staff and Parents' Race</li>
    </ul>
    <p>Last year, Awolowo House emerged as the overall winner with Ransome-Kuti House as the first runner-up. Which house will claim the trophy this year?</p>
    <p>Parents, guardians, alumni, and friends of the school are cordially invited to attend and support the students. Refreshments will be available.</p>
    <p>For more information, please contact the Sports Department or the House Masters.</p>
    <p>Come in your house colors and let's make this a memorable day of fun, fitness, and friendly competition!</p>`
}, {
  id: 2,
  slug: 'cultural-day',
  title: 'Cultural Day Celebration',
  date: '2023-12-05',
  time: '10:00 AM',
  location: 'School Hall',
  category: 'cultural',
  tags: ['Culture', 'Celebration', 'Tradition'],
  image: 'https://placehold.co/1200x800/darkblue/white?text=Cultural+Day',
  description: "A colorful celebration of Nigeria's diverse cultures featuring traditional dances, attire, and cuisine.",
  content: `<p>Isonyin Grammar School is pleased to announce our annual Cultural Day Celebration, scheduled for December 5, 2023, at the School Hall.</p>
    <p>Cultural Day is one of the most colorful and vibrant events in our school calendar. It's a day when we celebrate Nigeria's rich cultural diversity and heritage through various performances, exhibitions, and activities.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> December 5, 2023</li>
      <li><strong>Time:</strong> 10:00 AM - 3:00 PM</li>
      <li><strong>Venue:</strong> School Hall</li>
      <li><strong>Theme:</strong> "Celebrating Our Diversity, Strengthening Our Unity"</li>
    </ul>
    <h3>Program Highlights:</h3>
    <ul>
      <li>Traditional dance performances representing various Nigerian ethnic groups</li>
      <li>Cultural fashion parade showcasing traditional attires</li>
      <li>Exhibition of cultural artifacts and crafts</li>
      <li>Food fair featuring diverse Nigerian cuisines</li>
      <li>Cultural quiz competition</li>
      <li>Drama presentations depicting cultural values and traditions</li>
    </ul>
    <p>Students will be grouped according to different Nigerian cultures, not necessarily their own, to promote cross-cultural understanding and appreciation. Each group will showcase the dress, food, dance, music, and traditions of their assigned culture.</p>
    <p>Parents, guardians, alumni, and friends of the school are cordially invited to attend this colorful event. We encourage everyone to come dressed in traditional attire representing any Nigerian culture.</p>
    <p>For further inquiries, please contact the Cultural Day Planning Committee through the school office.</p>
    <p>Let's celebrate our rich cultural heritage together!</p>`
}, {
  id: 3,
  slug: 'carol-service',
  title: 'Christmas Carol Service',
  date: '2023-12-12',
  time: '4:00 PM',
  location: 'School Chapel',
  category: 'religious',
  tags: ['Christmas', 'Carol', 'Celebration'],
  image: 'https://placehold.co/1200x800/blue/white?text=Christmas+Carol',
  description: 'Annual carol service featuring performances by the school choir and drama group.',
  content: `<p>Isonyin Grammar School invites you to our Annual Christmas Carol Service, a special evening of music, reflection, and celebration of the Christmas season.</p>
    <p>Join us as our school choir, orchestra, and drama group present beautiful renditions of traditional and contemporary Christmas carols, along with dramatic presentations of the Christmas story.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> December 12, 2023</li>
      <li><strong>Time:</strong> 4:00 PM - 6:30 PM</li>
      <li><strong>Venue:</strong> School Chapel</li>
      <li><strong>Theme:</strong> "The Light of Christmas"</li>
    </ul>
    <h3>Program Highlights:</h3>
    <ul>
      <li>Processional hymn and lighting of the Advent candles</li>
      <li>Scripture readings recounting the Christmas story</li>
      <li>Choral performances by the Junior and Senior Choirs</li>
      <li>Instrumental performances by the School Orchestra</li>
      <li>Drama presentation: "The First Christmas"</li>
      <li>Community carol singing</li>
      <li>Special address by the Principal</li>
      <li>Benediction and recessional</li>
    </ul>
    <p>This event is open to all students, staff, parents, alumni, and friends of the school. Light refreshments will be served after the service.</p>
    <p>As part of our commitment to giving back to the community, we will be collecting non-perishable food items and toys to donate to local orphanages. We encourage attendees to bring along their donations.</p>
    <p>For more information, please contact the Music Department or the School Chaplain.</p>
    <p>Come and experience the joy and wonder of Christmas with us!</p>`
}];
const EventDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  useEffect(() => {
    // Simulate API call to fetch event
    setLoading(true);
    // Find the event with the matching slug
    const foundEvent = eventsData.find(event => event.slug === slug);
    if (foundEvent) {
      setEvent(foundEvent);
      // Find upcoming events (excluding current event)
      const upcoming = eventsData.filter(e => e.id !== foundEvent.id).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 3);
      setUpcomingEvents(upcoming);
    }
    setLoading(false);
  }, [slug]);
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = event?.title || 'Event at Isonyin Grammar School';
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
        break;
      default:
        // Copy link to clipboard
        navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!')).catch(err => console.error('Failed to copy: ', err));
    }
  };
  const handleAddToCalendar = () => {
    if (!event) return;
    alert('Calendar feature would open Google Calendar with this event');
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-4696">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-900" data-id="element-4697"></div>
      </div>;
  }
  if (!event) {
    return <div className="min-h-screen bg-white" data-id="element-4698">
        <PageHeader title="Event Not Found" description="The event you are looking for does not exist or has been moved." backgroundImage="https://placehold.co/1200x800/gray/white?text=Not+Found" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Events',
        path: '/events'
      }, {
        label: 'Not Found',
        path: `/events/${slug}`
      }]} data-id="element-4699" />
        <div className="container mx-auto px-4 py-12 text-center" data-id="element-4700">
          <h2 className="text-3xl font-bold text-blue-900 mb-6" data-id="element-4701">
            Event Not Found
          </h2>
          <p className="text-gray-700 mb-8" data-id="element-4702">
            The event you are looking for does not exist or has been moved.
          </p>
          <Button href="/events" variant="primary" icon={<ArrowLeftIcon data-id="element-4704" />} iconPosition="left" data-id="element-4703">
            Back to Events
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white" data-id="element-4705">
      <PageHeader title={event.title} description={event.description} backgroundImage={event.image} breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Events',
      path: '/events'
    }, {
      label: event.title,
      path: `/events/${event.slug}`
    }]} data-id="element-4706" />
      <div className="container mx-auto px-4 py-12" data-id="element-4707">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4708">
          <div className="lg:col-span-2" data-id="element-4709">
            <article className="bg-white rounded-lg shadow-md p-6 lg:p-8" data-id="element-4710">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-4711">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4712">
                  <div className="flex items-center" data-id="element-4713">
                    <CalendarIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-4714" />
                    <div data-id="element-4715">
                      <p className="text-sm text-gray-500" data-id="element-4716">Date</p>
                      <p className="font-bold text-gray-900" data-id="element-4717">
                        {formatDate(event.date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-4718">
                    <ClockIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-4719" />
                    <div data-id="element-4720">
                      <p className="text-sm text-gray-500" data-id="element-4721">Time</p>
                      <p className="font-bold text-gray-900" data-id="element-4722">{event.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-4723">
                    <MapPinIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-4724" />
                    <div data-id="element-4725">
                      <p className="text-sm text-gray-500" data-id="element-4726">Location</p>
                      <p className="font-bold text-gray-900" data-id="element-4727">
                        {event.location}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-blue-100" data-id="element-4728">
                  <Button onClick={handleAddToCalendar} variant="secondary" size="sm" data-id="element-4729">
                    Add to Calendar
                  </Button>
                  <Button href={`mailto:?subject=${encodeURIComponent(`Join me at ${event.title}`)}&body=${encodeURIComponent(`I'd like to invite you to ${event.title} at Isonyin Grammar School on ${formatDate(event.date)} at ${event.time}. More details: ${window.location.href}`)}`} variant="outline" size="sm" data-id="element-4730">
                    Invite Friends
                  </Button>
                </div>
              </div>
              <div className="prose prose-blue max-w-none mb-8" dangerouslySetInnerHTML={{
              __html: event.content
            }} data-id="element-4731" />
              <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-4732">
                <div className="flex flex-wrap justify-between items-center" data-id="element-4733">
                  <div className="mb-4 md:mb-0" data-id="element-4734">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-4735">
                      Tags:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-4736">
                      {event.tags.map((tag: string, index: number) => <Link key={index} to={`/events?tag=${tag}`} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm flex items-center transition-colors" data-id="element-4737">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-4738" />
                          {tag}
                        </Link>)}
                    </div>
                  </div>
                  <div data-id="element-4739">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-4740">
                      Share:
                    </p>
                    <div className="flex space-x-2" data-id="element-4741">
                      <button onClick={() => handleShare('facebook')} className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" aria-label="Share on Facebook" data-id="element-4742">
                        <FacebookIcon className="h-4 w-4" data-id="element-4743" />
                      </button>
                      <button onClick={() => handleShare('twitter')} className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" aria-label="Share on Twitter" data-id="element-4744">
                        <TwitterIcon className="h-4 w-4" data-id="element-4745" />
                      </button>
                      <button onClick={() => handleShare('linkedin')} className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" aria-label="Share on LinkedIn" data-id="element-4746">
                        <LinkedinIcon className="h-4 w-4" data-id="element-4747" />
                      </button>
                      <button onClick={() => handleShare('copy')} className="p-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors" aria-label="Copy link" data-id="element-4748">
                        <ShareIcon className="h-4 w-4" data-id="element-4749" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="mt-8" data-id="element-4750">
              <Button href="/events" variant="outline" icon={<ArrowLeftIcon data-id="element-4752" />} iconPosition="left" data-id="element-4751">
                Back to Events
              </Button>
            </div>
          </div>
          <div data-id="element-4753">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-4754">
              <h3 className="text-xl font-bold text-blue-900 mb-4 border-b border-gray-200 pb-2" data-id="element-4755">
                Upcoming Events
              </h3>
              {upcomingEvents.length > 0 ? <div className="space-y-4" data-id="element-4756">
                  {upcomingEvents.map(upcomingEvent => <div key={upcomingEvent.id} className="flex items-start" data-id="element-4757">
                      <div className="bg-blue-100 text-blue-900 font-bold rounded p-2 text-center min-w-[50px] mr-3 flex-shrink-0" data-id="element-4758">
                        <div className="text-lg" data-id="element-4759">
                          {new Date(upcomingEvent.date).getDate()}
                        </div>
                        <div className="text-xs" data-id="element-4760">
                          {new Date(upcomingEvent.date).toLocaleString('default', {
                      month: 'short'
                    }).toUpperCase()}
                        </div>
                      </div>
                      <div data-id="element-4761">
                        <h4 className="font-bold text-blue-900 hover:text-blue-700 transition-colors line-clamp-2" data-id="element-4762">
                          <Link to={`/events/${upcomingEvent.slug}`} data-id="element-4763">
                            {upcomingEvent.title}
                          </Link>
                        </h4>
                        <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-4764">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-4765" />
                          <span data-id="element-4766">{upcomingEvent.time}</span>
                        </div>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-700" data-id="element-4767">No upcoming events.</p>}
              <div className="mt-6" data-id="element-4768">
                <Button href="/events" variant="outline" size="sm" className="w-full" data-id="element-4769">
                  View All Events
                </Button>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-lg p-6" data-id="element-4770">
              <div className="flex items-center mb-4" data-id="element-4771">
                <UsersIcon className="h-6 w-6 text-yellow-400 mr-3" data-id="element-4772" />
                <h3 className="text-xl font-bold" data-id="element-4773">Interested in Attending?</h3>
              </div>
              <p className="mb-6" data-id="element-4774">
                Let us know if you're planning to attend this event. This helps
                us with planning and preparations.
              </p>
              <form className="space-y-3" onSubmit={e => {
              e.preventDefault();
              alert('Thank you for your interest! We look forward to seeing you at the event.');
            }} data-id="element-4775">
                <div data-id="element-4776">
                  <input type="text" placeholder="Your Name" className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-4777" />
                </div>
                <div data-id="element-4778">
                  <input type="email" placeholder="Your Email" className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-4779" />
                </div>
                <div data-id="element-4780">
                  <select className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-4781">
                    <option value="" data-id="element-4782">Number of Attendees</option>
                    <option value="1" data-id="element-4783">1</option>
                    <option value="2" data-id="element-4784">2</option>
                    <option value="3" data-id="element-4785">3</option>
                    <option value="4" data-id="element-4786">4</option>
                    <option value="5+" data-id="element-4787">5+</option>
                  </select>
                </div>
                <Button type="submit" variant="primary" className="w-full" data-id="element-4788">
                  I'll Be There
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default EventDetail;